/**
 * PayTR iFrame Checkout Page
 *
 * Loaded after backend creates a PayTR token. Embeds PayTR's secure iframe
 * inside our own page so customers stay in Paydaai's branded experience.
 *
 * Query params:
 *   - url     : the full PayTR iframe URL (https://www.paytr.com/odeme/guvenli/{token})
 *   - return  : path to navigate back to on cancel (default: /admin/dashboard)
 *   - title   : optional header label (e.g. "Plan Yükseltme")
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Lock, ShieldCheck } from 'lucide-react';
import paytrLogo from '@/assets/paytr-logo.svg';

export const Checkout = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const [loaded, setLoaded] = useState(false);

  const paytrUrl = params.get('url') || '';
  const returnPath = params.get('return') || '/admin/dashboard';
  const title = params.get('title') || 'Güvenli Ödeme';

  // Load PayTR's iframe resizer once
  useEffect(() => {
    if (document.getElementById('paytr-iframe-resizer')) return;
    const script = document.createElement('script');
    script.id = 'paytr-iframe-resizer';
    script.src = 'https://www.paytr.com/js/iframeResizer.min.js';
    script.onload = () => {
      if (window.iFrameResize && iframeRef.current) {
        window.iFrameResize({}, '#paytriframe');
      }
    };
    document.body.appendChild(script);
  }, []);

  const safeUrl = useMemo(() => {
    // Only allow PayTR's secure domain — defence against open-redirect injection
    if (!paytrUrl) return '';
    try {
      const u = new URL(paytrUrl);
      if (u.hostname.endsWith('paytr.com')) return paytrUrl;
    } catch {
      return '';
    }
    return '';
  }, [paytrUrl]);

  if (!safeUrl) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-6">
        <div className="max-w-md text-center bg-white rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.05)] p-8 border border-[#E2E8F0]">
          <p className="text-lg font-semibold text-[#0F172A] mb-2">Ödeme bağlantısı geçersiz</p>
          <p className="text-sm text-[#64748B] mb-6">Lütfen tekrar deneyin. Sorun devam ederse destek ekibimizle iletişime geçin.</p>
          <Button onClick={() => navigate(returnPath)} className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white" data-testid="checkout-back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Geri Dön
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="paytr-checkout-page">
      {/* Top bar */}
      <header className="bg-white border-b border-[#E2E8F0] sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <button
            type="button"
            onClick={() => navigate(returnPath)}
            className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#0F172A] transition-colors"
            data-testid="checkout-back-button"
          >
            <ArrowLeft className="w-4 h-4" /> Vazgeç
          </button>
          <div className="flex items-center gap-2">
            <span className="text-[15px] font-semibold text-[#0F172A]">{title}</span>
          </div>
          <div className="w-[68px]" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-12">
        {/* Trust banner */}
        <div className="flex items-start gap-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded-xl px-4 py-3 mb-4" data-testid="checkout-trust-banner">
          <ShieldCheck className="w-5 h-5 text-[#2563EB] shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold text-[#1E3A8A]">Güvenli Ödeme</p>
            <p className="text-[#1E40AF]/90 text-[13px] leading-relaxed">
              Kart bilgileriniz PayTR güvencesi altında işlenir. Paydaai sunucularında saklanmaz, 3D Secure ile doğrulanır.
            </p>
          </div>
        </div>

        {/* PayTR iframe */}
        <div
          className="bg-white rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.05)] border border-[#E2E8F0] overflow-hidden"
          data-testid="checkout-iframe-wrapper"
        >
          {!loaded && (
            <div className="flex items-center justify-center py-20 text-sm text-[#64748B]">
              <Lock className="w-4 h-4 mr-2 animate-pulse" /> Ödeme formu yükleniyor…
            </div>
          )}
          <iframe
            ref={iframeRef}
            id="paytriframe"
            title="PayTR Ödeme Formu"
            src={safeUrl}
            frameBorder="0"
            scrolling="no"
            onLoad={() => {
              setLoaded(true);
              if (window.iFrameResize) {
                try { window.iFrameResize({}, '#paytriframe'); } catch (_) { /* noop */ }
              }
            }}
            style={{ width: '100%', minHeight: loaded ? '700px' : '0px', display: loaded ? 'block' : 'none' }}
            data-testid="paytr-iframe"
          />
        </div>

        {/* Powered by */}
        <div className="flex items-center justify-center gap-1.5 mt-5">
          <span className="text-[11px] text-[#94A3B8]">Powered by</span>
          <img src={paytrLogo} alt="PayTR" className="h-4 opacity-90" data-testid="paytr-footer-logo" />
        </div>
        <p className="text-[11px] text-[#94A3B8] text-center mt-2">
          Paydaai bir ödeme kuruluşu değildir. Tüm ödemeler PayTR Ödeme Hizmetleri A.Ş. tarafından işlenmektedir.
        </p>
      </main>
    </div>
  );
};

export default Checkout;
